/* eslint-disable no-nested-ternary */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { CircularProgress, Skeleton, Tooltip } from "@mui/material";
import { RootStateOrAny, useSelector } from "react-redux";

import * as IconsSVG from "assets/iconsSVG";
import NewTaskV2 from "components/dialogCommon/templates/NewTaskV2";
import DialogCommon from "components/dialogCommon";
import { AuthState } from "store/ducks/auth/types";
import {
  ResumoCorretorTarefas,
  useTarefasGerenciais,
  useTarefasGerenciaisPorCorretor,
} from "services/api/Common_TarefasLeads/tarefas-gerenciais";
import { CardTarefas } from "../cardTarefas";
import * as HomeStyles from "../../styles";
import * as Styles from "./styles";

type TaskStatusFilter = "hoje" | "atrasadas";

const getQtdAtrasadasCorretor = (corretor: ResumoCorretorTarefas) =>
  corretor.qtdTarefasAtrasadas ?? corretor.qtdTarefasAtrasas ?? 0;

const SectionManagementTasks = () => {
  const { userId, idAD } = useSelector<RootStateOrAny, AuthState>(
    (state) => state.user
  );

  const [newTask, setNewTask] = useState<boolean>(false);
  const [filter, setFilter] = useState<{
    status: TaskStatusFilter;
  }>({
    status: "hoje",
  });
  const [selectedBroker, setSelectedBroker] =
    useState<ResumoCorretorTarefas | null>(null);

  const idCorretor365 = userId || idAD || "";
  const selectedBrokerId = selectedBroker?.idCorretor365 || "";

  const visaoGeralQuery = useMemo(
    () => ["tarefas-gerenciais", idCorretor365].join("-"),
    [idCorretor365]
  );

  const porCorretorQuery = useMemo(
    () =>
      ["tarefas-gerenciais-por-corretor", selectedBrokerId, filter.status].join(
        "-"
      ),
    [selectedBrokerId, filter.status]
  );

  const {
    data: visaoGeralData,
    isFetching: isFetchingVisaoGeral,
    refetch,
  } = useTarefasGerenciais({
    queryKey: visaoGeralQuery,
    enabled: !selectedBroker,
  });

  const {
    data: porCorretorData,
    isFetching: isFetchingPorCorretor,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useTarefasGerenciaisPorCorretor({
    queryKey: porCorretorQuery,
    idCorretor365: selectedBrokerId,
    atrasadas: filter.status === "atrasadas",
    hoje: filter.status === "hoje",
    totalItensRetorno: 10,
  });

  const isLoadingFirstPage = selectedBroker
    ? isFetchingPorCorretor && !isFetchingNextPage
    : isFetchingVisaoGeral && !visaoGeralData;

  const tarefas = useMemo(
    () =>
      selectedBroker
        ? (porCorretorData?.pages ?? []).flatMap((page) => page.tarefas)
        : [],
    [selectedBroker, porCorretorData?.pages]
  );

  const sentinelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!selectedBroker) return undefined;
    const node = sentinelRef.current;
    if (!node) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { root: null, rootMargin: "0px", threshold: 0.1 }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [
    selectedBroker,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    tarefas.length,
  ]);
  const corretores = useMemo(() => {
    const occurrences = new Map<string, number>();

    return (visaoGeralData?.data?.resumosCorretores ?? []).map((corretor) => {
      const baseKey = corretor.idCorretor365 || corretor.nomeCorretor;
      const occurrence = occurrences.get(baseKey) ?? 0;

      occurrences.set(baseKey, occurrence + 1);

      return {
        ...corretor,
        cardKey: `${filter.status}-${baseKey}-${occurrence}`,
      };
    });
  }, [visaoGeralData?.data?.resumosCorretores, filter.status]);
  const showTasks = Boolean(selectedBroker);
  const selectedCount = tarefas.length;
  const listTitle = showTasks
    ? `${filter.status === "hoje" ? "Para hoje" : "Atrasadas"} - ${
        selectedBroker?.nomeCorretor
      }`
    : filter.status === "hoje"
    ? "Vencem hoje por corretor"
    : "Corretores com atraso";

  const handleFilterStatus = (status: TaskStatusFilter) => {
    setFilter((current) => ({
      ...current,
      status,
    }));
    setSelectedBroker(null);
  };

  const renderSkeleton = () => (
    <>
      {[0, 1, 2, 3, 4].map((item) => (
        <Styles.SkeletonCard key={item}>
          <Skeleton variant="circular" width={40} height={40} />
          <Styles.SkeletonInfo>
            <Skeleton variant="text" width="70%" height={22} />
            <Skeleton variant="text" width="38%" height={18} />
          </Styles.SkeletonInfo>
          <Skeleton variant="text" width={32} height={28} />
        </Styles.SkeletonCard>
      ))}
    </>
  );

  return (
    <HomeStyles.SectionTasks>
      <HomeStyles.HeaderTasks>
        <HomeStyles.Row>
          <IconsSVG.NewTaskDialog />
          <HomeStyles.TasksTitle>Tarefas da Equipe</HomeStyles.TasksTitle>
        </HomeStyles.Row>
        <Tooltip title="Criar tarefa" placement="top" followCursor arrow>
          <HomeStyles.Button
            className="plus-btn"
            onClick={() => setNewTask(true)}
          >
            <IconsSVG.PlusWhite />
          </HomeStyles.Button>
        </Tooltip>
      </HomeStyles.HeaderTasks>

      <Styles.SummaryRow>
        <Styles.SummaryCard
          className={filter.status === "atrasadas" ? "danger active" : "danger"}
          onClick={() => handleFilterStatus("atrasadas")}
        >
          <Styles.SummaryNumber>
            {visaoGeralData?.data?.qtdTarefasAtrasadas ?? 0}
          </Styles.SummaryNumber>
          <Styles.SummaryLabel>Atrasadas</Styles.SummaryLabel>
        </Styles.SummaryCard>
        <Styles.SummaryCard
          className={filter.status === "hoje" ? "active" : ""}
          onClick={() => handleFilterStatus("hoje")}
        >
          <Styles.SummaryNumber>
            {visaoGeralData?.data?.qtdTarefasHoje ?? 0}
          </Styles.SummaryNumber>
          <Styles.SummaryLabel>Vencem hoje</Styles.SummaryLabel>
        </Styles.SummaryCard>
      </Styles.SummaryRow>

      <Styles.ListTitle>
        <Styles.ListTitleText>{listTitle}</Styles.ListTitleText>
        {showTasks && (
          <Styles.ResultPill>{selectedCount} resultados</Styles.ResultPill>
        )}
      </Styles.ListTitle>

      <HomeStyles.ContentTarefas>
        {isLoadingFirstPage ? (
          renderSkeleton()
        ) : showTasks ? (
          tarefas.length > 0 ? (
            <>
              {tarefas.map((item: any) => (
                <CardTarefas
                  key={item.idTarefaLead}
                  title={item.titulo}
                  lead={item.nomeLead}
                  tipoTarefa={item.tipoTarefa}
                  statusTarefa={item.descricaoStatusTarefa}
                  data={item.dataHoraProgramadaRealizacaoTarefa}
                  dataCriacaoTarefa={item?.dataCriacaoTarefa}
                  dataConclusao={item.dataHoraConclusaoTarefa}
                  obs={item.observacao}
                  tipoConclusao={item.tipoResultadoTarefa}
                  etapa={item.descricaoEtapaFunilLead}
                  setEditTask={() => {}}
                  editTask={false}
                  urlAnexo={item.urlAnexo ?? item?.anexo?.urlAnexo}
                  onItemEdit={() => {}}
                  setConcludeTask={() => {}}
                  concludeTask={false}
                  onItemConclude={() => {}}
                  descricao={item.descricao}
                  empreendimento={item.empreendimento}
                  tipoResultadoTarefa={item.tipoResultadoTarefa}
                  idLeadCorretor={item.idLeadCorretor}
                  telefonePrincipal={item.telefonePrincipal}
                  emailPrincipal={item.emailPrincipal}
                  hideActions
                />
              ))}
              {hasNextPage && (
                <div
                  ref={sentinelRef}
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    padding: "16px 0",
                  }}
                >
                  {isFetchingNextPage && (
                    <CircularProgress size={20} style={{ color: "#2d5bff" }} />
                  )}
                </div>
              )}
            </>
          ) : (
            <HomeStyles.Text
              style={{
                textAlign: "center",
                marginTop: 40,
                paddingBottom: 40,
              }}
            >
              Sem tarefas para esse corretor
            </HomeStyles.Text>
          )
        ) : corretores.length > 0 ? (
          corretores.map((corretor) => {
            const count =
              filter.status === "hoje"
                ? corretor.qtdTarefasHoje ?? 0
                : getQtdAtrasadasCorretor(corretor);

            return (
              <Styles.BrokerCard
                key={corretor.cardKey}
                onClick={() => setSelectedBroker(corretor)}
              >
                <Styles.Avatar>
                  {corretor.fotoUrlCorretor ? (
                    <img
                      src={corretor.fotoUrlCorretor}
                      alt={corretor.nomeCorretor}
                    />
                  ) : (
                    <IconsSVG.UserIcon />
                  )}
                </Styles.Avatar>
                <Styles.BrokerInfo>
                  <Styles.BrokerName>{corretor.nomeCorretor}</Styles.BrokerName>
                  <Styles.BrokerLeads>
                    {corretor.qtdLeads} leads
                  </Styles.BrokerLeads>
                </Styles.BrokerInfo>
                <Styles.BrokerCount>
                  <Styles.CountNumber>{count}</Styles.CountNumber>
                  <Styles.CountLabel>Tarefas</Styles.CountLabel>
                </Styles.BrokerCount>
              </Styles.BrokerCard>
            );
          })
        ) : (
          <HomeStyles.Text
            style={{
              textAlign: "center",
              marginTop: 40,
              paddingBottom: 40,
            }}
          >
            Sem tarefas da equipe
          </HomeStyles.Text>
        )}
      </HomeStyles.ContentTarefas>

      <DialogCommon
        closeText="Fechar"
        saveText="Salvar"
        onClose={() => setNewTask}
        onSave={() => {}}
        isRight={false}
        onClean={() => {}}
        titleHeader="Agendar tarefa"
        isFilter={false}
        isClose
        width={500}
        maxHeight="680px"
        component={
          <NewTaskV2
            idLead=""
            idLeadCorretor=""
            lead={null}
            dataLead={null}
            closeModal={() => setNewTask(false)}
            refetch={() => refetch()}
          />
        }
        setOpenModal={setNewTask}
        open={newTask}
        icon={<IconsSVG.NewTaskDialog />}
      />
    </HomeStyles.SectionTasks>
  );
};

export default SectionManagementTasks;
